// camera.js
// OPTIMIZATION START - Frame skipping and quality profiles
import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';
import { Camera } from '@mediapipe/camera_utils';
import { initPoseDetection, detectPose } from './PoseDetection';
import { spawn_apple } from './Apple';
import SegmentationProcessor from './Segmentation';
import { detectExercise } from '../Exercise/ExerciseDetection.js';
import { getQualityProfile } from '../config/QualityProfiles.js';
import DebugLogger from '../utils/DebugLogger.js';

let canvas, ctx, video, imageSegmenter, stream, camera, animationFrameId;
const segProcessor = new SegmentationProcessor();

// KNOX DATA CLEAR DETECTION: localStorage marker to detect external data clearing
const INIT_MARKER_KEY = 'mediapipe_init_marker';

// Dynamic resolution based on quality profile
let WIDTH, HEIGHT;
let segmentationFrameCount = 0;
let poseFrameCount = 0;
// OPTIMIZATION END

export async function init(module, XR = false, canvasId = "camera_canvas") {
    const gameModule = module;
    // WebView needs more time for MediaPipe/WASM loading than regular Chrome
    const isWebView = /wv|WebView/i.test(navigator.userAgent);
    const INIT_TIMEOUT = isWebView ? 20000 : 10000; // 20s for WebView, 10s for Chrome per step

    DebugLogger.log("Camera", `Initializing camera (XR: ${XR}, canvasId: ${canvasId})`);
    DebugLogger.log("Camera", `Environment: ${isWebView ? 'WebView' : 'Browser'}, Per-step timeout: ${INIT_TIMEOUT}ms`);

    // ========== KNOX DATA CLEAR DETECTION ==========
    // Check if localStorage was cleared while instances still in memory
    // This catches Knox instant restart: back button → data cleared → restart 5s later
    let initMarker = null;
    try {
        initMarker = localStorage.getItem(INIT_MARKER_KEY);
    } catch(e) {
        DebugLogger.log("Camera", "Cannot read localStorage (private mode?), skipping marker check");
    }

    const hasInstances = !!(imageSegmenter || camera || stream);

    if (!initMarker && hasInstances) {
        // CRITICAL: Data cleared externally but instances exist = KNOX CLEARED DATA
        DebugLogger.log("Camera", "🚨 KNOX DATA CLEAR DETECTED - instances exist but marker missing");
        console.error('[Camera] External data clearing detected! Forcing cleanup of corrupted instances...');

        // Force cleanup of corrupted instances
        try {
            if (imageSegmenter) {
                imageSegmenter.close();
                DebugLogger.log("Camera", "Closed corrupted imageSegmenter");
            }
        } catch(e) {
            DebugLogger.log("Camera", "imageSegmenter.close() failed (expected):", e.message);
        }

        try {
            if (camera) {
                await camera.stop();
                DebugLogger.log("Camera", "Stopped corrupted camera");
            }
        } catch(e) {
            DebugLogger.log("Camera", "camera.stop() failed:", e.message);
        }

        if (stream) {
            stream.getTracks().forEach(t => {
                try { t.stop(); } catch(e) {}
            });
            DebugLogger.log("Camera", "Stopped corrupted stream tracks");
        }

        // Force null everything
        imageSegmenter = null;
        camera = null;
        stream = null;
        video = null;
        ctx = null;
        canvas = null;
        animationFrameId = null;

        DebugLogger.log("Camera", "Forced cleanup complete, will reinitialize from scratch");
    }

    try {
        // OPTIMIZATION: Get quality profile for adaptive settings
        const quality = getQualityProfile?.() || {
            camera: { width: 1920, height: 1080, frameRate: 60 },
            segmentation: { processEveryFrame: 1 },
            pose: { processEveryFrame: 1, delegate: 'GPU' }
        };

        // Set dynamic resolution
        WIDTH = quality.camera.width;
        HEIGHT = quality.camera.height;

        // Reset frame counters
        segmentationFrameCount = 0;
        poseFrameCount = 0;

        DebugLogger.log("Camera", "Quality profile:", quality);
        DebugLogger.log("Camera", `Device GPU: ${quality.pose.delegate}, Camera: ${WIDTH}x${HEIGHT}`);

        console.log(`[Camera] Initializing with quality profile:`, {
            resolution: `${WIDTH}x${HEIGHT}`,
            delegate: quality.pose.delegate,
            segmentationEvery: quality.segmentation.processEveryFrame,
            poseEvery: quality.pose.processEveryFrame,
            fullQuality: quality
        });

        // Initialize the canvas where the final composited output will be rendered.
        ({ canvas, ctx } = initializeCanvas(canvasId));
        video = document.createElement('video');
        video.autoplay = true;
        video.muted = true;

        if(!XR){
            // Load the Mediapipe vision tasks and the segmentation model.
            DebugLogger.log("Camera", "Loading MediaPipe vision tasks...");
            const visionPromise = FilesetResolver.forVisionTasks(
                "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
            );
            const vision = await Promise.race([
                visionPromise,
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('MediaPipe vision loading timed out')), INIT_TIMEOUT)
                )
            ]);

            // CRITICAL: Close existing imageSegmenter before creating new one to prevent memory leak
            if (imageSegmenter) {
                DebugLogger.log("Camera", "Closing existing ImageSegmenter before creating new one");
                try {
                    imageSegmenter.close();
                    imageSegmenter = null;
                } catch (error) {
                    console.warn('Error closing existing ImageSegmenter:', error);
                }
            }

            // OPTIMIZATION: Use delegate from quality profile
            DebugLogger.log("Camera", `Initializing ImageSegmenter with ${quality.pose.delegate} delegate...`);
            const segmenterPromise = ImageSegmenter.createFromOptions(vision, {
                baseOptions: {
                    modelAssetPath:
                        "https://storage.googleapis.com/mediapipe-tasks/image_segmenter/selfie_segmentation_landscape.tflite",
                    delegate: quality.pose.delegate // Use quality profile delegate
                },
                outputCategoryMask: true,
                runningMode: "VIDEO"
            });
            imageSegmenter = await Promise.race([
                segmenterPromise,
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('ImageSegmenter initialization timed out')), INIT_TIMEOUT)
                )
            ]);
            DebugLogger.log("Camera", "ImageSegmenter initialized successfully");

            // OPTIMIZATION: Initialize pose detection with quality profile delegate
            DebugLogger.log("Camera", "Initializing pose detection...");
            await initPoseDetection(quality.pose.delegate);
            DebugLogger.log("Camera", "Pose detection initialized");

            // NOTE: DO NOT initialize exercise detection here!
            // Each game (Apple.js, etc.) initializes it with their own callbacks
            // If we initialize here, callbacks registered later won't work

            // Request the video stream from the user's webcam.
            DebugLogger.log("Camera", "Requesting camera access...");
            stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: WIDTH },
                    height: { ideal: HEIGHT },
                    frameRate: { ideal: quality.camera.frameRate, max: quality.camera.frameRate }
                }
            });
            DebugLogger.log("Camera", `Camera access granted: ${stream.getVideoTracks()[0].label}`);

            video.srcObject = stream;
            await new Promise((resolve, reject) => {
                video.onloadedmetadata = () => {
                    // Ensure video has valid dimensions
                    if (video.videoWidth > 0 && video.videoHeight > 0) {
                        resolve();
                    } else {
                        reject(new Error("Video dimensions are invalid"));
                    }
                };
                video.onerror = reject;
                
                // Add timeout to prevent hanging
                setTimeout(() => {
                    reject(new Error("Video loading timeout"));
                }, 10000);
            });
            DebugLogger.log("Camera", `Video ready: ${video.videoWidth}x${video.videoHeight}`);

            // Ensure video is playing
            await video.play();
            DebugLogger.log("Camera", "Video playback started");

            // Set up the Mediapipe Camera which calls the segmentation processor on every frame.
            DebugLogger.log("Camera", "Setting up MediaPipe Camera...");
            camera = new Camera(video, {
                onFrame: async () => {
                    // Check if video is ready for processing
                    if (!video || video.videoWidth === 0 || video.videoHeight === 0 ||
                        video.readyState < video.HAVE_ENOUGH_DATA) {
                        return;
                    }

                    // Reinitialize the canvas context if needed.
                    if (!ctx) ({ ctx } = initializeCanvas(canvasId));
                    const timestamp = performance.now();

                    // OPTIMIZATION: Frame skipping for segmentation
                    segmentationFrameCount++;
                    const shouldProcessSegmentation = (segmentationFrameCount % quality.segmentation.processEveryFrame) === 0;

                    try {
                        if (shouldProcessSegmentation) {
                            // Get the segmentation results and process the mask.
                            const segmentationResults = await imageSegmenter.segmentForVideo(video, timestamp);
                            await segProcessor.processSegmentation(segmentationResults, ctx, video, WIDTH, HEIGHT);
                        } else {
                            // OPTIMIZATION: Redraw cached frame to prevent flicker
                            segProcessor.redrawLastFrame(ctx, video, WIDTH, HEIGHT);
                        }

                        // OPTIMIZATION: Frame skipping for pose detection
                        poseFrameCount++;
                        const shouldProcessPose = (poseFrameCount % quality.pose.processEveryFrame) === 0;

                        if (shouldProcessPose) {
                            // Use exercise detection system for FSM-based gesture recognition
                            try {
                                const exerciseResult = await detectExercise(video, timestamp);

                                // Pass pose landmarks to tomato game for collision detection
                                if (exerciseResult && exerciseResult.landmarks && gameModule.updatePoseLandmarks) {
                                    gameModule.updatePoseLandmarks(exerciseResult.landmarks);
                                }

                                // The FSM will automatically trigger repetition callbacks when exercises are detected
                                // No need to manually call gameModule.on_gesture() - the FSM handles it
                            } catch (exerciseError) {
                                // Fallback to old hand detection if exercise detection fails
                                const isHandRaised = await detectPose(video, timestamp);
                                if (isHandRaised) {
                                    gameModule.on_gesture();
                                }
                            }
                        }
                    } catch (error) {
                        // Log errors to help with debugging
                        console.error('[Camera] Frame processing error:', error);
                    }
                },
                width: WIDTH,
                height: HEIGHT
            });

            await camera.start();
            DebugLogger.log("Camera", "MediaPipe Camera started successfully");
        }
        else {
            // XR mode - Request the video stream from the user's webcam.
            DebugLogger.log("Camera", "XR mode - initializing video stream");
            stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: WIDTH },
                    height: { ideal: HEIGHT },
                    frameRate: { ideal: quality.camera.frameRate, max: quality.camera.frameRate }
                }
            });

            video.srcObject = stream;
            if (!ctx) ({ ctx } = initializeCanvas(canvasId));
            
            // Wait for video to be ready before starting the drawing loop
            await new Promise((resolve, reject) => {
                video.onloadedmetadata = resolve;
                video.onerror = reject;
            });
            
            // Ensure video is playing
            await video.play();
            
            // Start rendering the video stream to canvas
            function drawFrame() {
                if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
                    // Add a small crop margin (e.g., 12% on each side)
                    const cropMargin = 0.12;
                    
                    ctx.drawImage(
                        video,
                        0,                                                // Destination X
                        0,                                                // Destination Y
                        WIDTH,                                           // Destination width
                        HEIGHT                                           // Destination height
                    );
                }
                animationFrameId = requestAnimationFrame(drawFrame);
            }
            
            drawFrame();
        }

        DebugLogger.log("Camera", "✅ Camera initialization completed successfully");

        // Set init marker AFTER successful initialization
        try {
            const newMarker = `${Date.now()}_${Math.random()}`;
            localStorage.setItem(INIT_MARKER_KEY, newMarker);
            DebugLogger.log("Camera", "Init marker set:", newMarker.substring(0, 20) + "...");
        } catch(e) {
            DebugLogger.log("Camera", "Cannot set init marker (private mode?)");
        }
    } catch (error) {
        DebugLogger.error("Camera", "Camera initialization failed", error);
        console.error("Error in initialization:", error);
        throw error;
    }
}

// Helper function to initialize the canvas element.
function initializeCanvas(canvasId) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) throw new Error("Canvas element not found");

    canvas.width = WIDTH;
    canvas.height = HEIGHT;

    const ctx = canvas.getContext("2d", {
        willReadFrequently: true,
        alpha: true
    });

    return { canvas, ctx };
}

// Cleanly stop the video stream when needed.
export async function close_stream() {
    // 1. Cancel XR animation frame if running
    if (animationFrameId) {
        console.log('[Camera] Canceling XR animation frame...');
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }

    // 2. Stop the MediaPipe Camera (stops frame processing)
    if (camera) {
        try {
            DebugLogger.log("Camera", "Stopping MediaPipe camera");
            await camera.stop(); // CRITICAL: Wait for camera to fully stop
            camera = null;
        } catch (error) {
            console.warn('Error stopping MediaPipe camera:', error);
        }
    }

    // 3. THEN dispose MediaPipe ImageSegmenter to release WASM memory
    if (imageSegmenter) {
        try {
            DebugLogger.log("Camera", "Closing ImageSegmenter to release WASM memory");
            imageSegmenter.close();
            imageSegmenter = null;
        } catch (error) {
            console.warn('Error closing ImageSegmenter:', error);
        }
    }

    // 4. DON'T clean up segmentation processor - it's a singleton that persists between games
    // We only need to clean up ImageSegmenter (WASM memory), not the canvas processor

    // 5. Stop and clean up the video stream
    if (stream) {
        stream.getTracks().forEach((track) => {
            track.stop();
        });
        stream = null;
    }

    // 6. Pause video and clear its source
    if (video) {
        video.pause();
        video.srcObject = null;
        video.src = '';
        video.load(); // This helps ensure the video element is completely reset
    }

    // 7. Clear the canvas
    if (ctx && canvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    // 8. CRITICAL: Add delay for WebView to fully release resources
    // WebView needs more time than Chrome to clean up MediaPipe/WASM
    const isWebView = /wv|WebView/i.test(navigator.userAgent);
    if (isWebView) {
        DebugLogger.log("Camera", "Waiting 200ms for WebView resource cleanup");
        await new Promise(resolve => setTimeout(resolve, 200));
    }

    // 9. Clear init marker on cleanup
    try {
        localStorage.removeItem(INIT_MARKER_KEY);
        DebugLogger.log("Camera", "Init marker cleared");
    } catch(e) {
        // Ignore if localStorage not available
    }

    DebugLogger.log("Camera", "close_stream() complete");
}